import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canSeeRevenue } from '../../lib/perms';
import { commandCenterApi, applicationsApi, getErrorMessage } from '../../services/api';
import { useStickyOwnerLens } from './OwnerLensToggle';
import { formatINR, DRILLABLE_METRICS, DRILL_TITLES } from '../command-center/constants';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../ui/tooltip';
import {
  Send, Calendar, Award, TrendingUp, Clock, ListTodo, FileText, CalendarClock,
  IndianRupee, Info, ExternalLink, SlidersHorizontal, Loader2, CheckCircle2,
  XCircle, User,
} from 'lucide-react';
import TodaysWinBanner from '../TodaysWinBanner';
import { toast } from 'sonner';

// Self-contained Recruiter Command Center summary strip — owns its own
// metrics fetch, range filter, customize popover, drill-down dialog and
// TodaysWin banner. Designed to be mounted anywhere (currently the
// Dashboard hero) without depending on the kanban below.
//
// Drill-down row clicks route the user to /command-center with the picked
// candidate via query params so the existing CC selection logic takes over.
const CommandCenterSummary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ['admin', 'delivery_manager', 'team_lead'].includes(user?.role);
  const showRevenue = canSeeRevenue(user?.role);
  const [ownerLens] = useStickyOwnerLens('owner_lens', 'created');

  // ---------- Metrics fetch ----------
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [ccRangeActive, setCcRangeActive] = useState(false);
  const [ccStart, setCcStart] = useState('');
  const [ccEnd, setCcEnd] = useState('');

  const fetchMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const params =
        ccRangeActive && ccStart && ccEnd && ccStart <= ccEnd
          ? { start_date: ccStart, end_date: ccEnd }
          : {};
      if (isManager) params.owner_lens = ownerLens;
      const m = await commandCenterApi.getMetrics(params);
      setMetrics(m.data || null);
    } catch (e) {
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [ccRangeActive, ccStart, ccEnd, isManager, ownerLens]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // ---------- KPI tile config (mirrors the original CC strip) ----------
  const metricTiles = useMemo(() => {
    const m = metrics || {};
    const rangeOn = !!m.range_active;
    const rangeTag = rangeOn ? 'in range' : null;
    const subLabel = rangeOn ? `Submissions ${rangeTag}` : 'Submissions';
    const ivLabel = rangeOn ? `Interviews ${rangeTag}` : 'Interviews';
    const closeLabel = rangeOn ? `Closures ${rangeTag}` : 'Closures';
    const convLabel = rangeOn ? `Conversion ${rangeTag}` : 'Conversion';
    const tiles = [
      { key: 'submissions', label: subLabel, value: m.submissions_today ?? '—', icon: Send,
        color: 'text-sky-600 bg-sky-50 border-sky-100',
        description: rangeOn ? 'Applications created within the selected range.' : 'Applications created today.' },
      { key: 'interviews', label: ivLabel, value: m.interviews_today ?? '—', icon: Calendar,
        color: 'text-amber-600 bg-amber-50 border-amber-100',
        description: rangeOn ? 'Interview Scheduled/Done events within the range.' : 'Interviews scheduled or completed today.' },
      { key: 'closures', label: closeLabel, value: m.closures_this_month ?? '—', icon: Award,
        color: 'text-emerald-600 bg-emerald-50 border-emerald-100',
        description: rangeOn ? 'JOINED stage events inside the selected range.' : 'Joins recorded this calendar month.' },
      { key: 'conversion', label: convLabel,
        value: m.conversion_pct != null ? `${m.conversion_pct}%` : '—', icon: TrendingUp,
        color: 'text-violet-600 bg-violet-50 border-violet-100',
        description: 'Joined ÷ Submissions × 100 for the current scope window.' },
      { key: 'tat', label: 'Avg TAT', value: m.tat_days != null ? `${m.tat_days}` : '—', icon: Clock,
        color: 'text-teal-600 bg-teal-50 border-teal-100',
        description: 'Average days sourced → joined this month. Lower is better.' },
      { key: 'pending', label: 'Pending tasks', value: m.pending_tasks_count ?? '—', icon: ListTodo,
        color: 'text-orange-600 bg-orange-50 border-orange-100',
        description: 'Stale screenings, today\'s interviews, pending offer feedback, and offers without a joining decision.' },
    ];
    if (m.pending_paperwork_count != null && m.pending_paperwork_count > 0) {
      const stale = m.pending_paperwork_stale_count || 0;
      tiles.push({
        key: 'pending_paperwork', label: 'Pending paperwork',
        badge: stale > 0 ? `${stale} stale` : null, value: m.pending_paperwork_count, icon: FileText,
        color: stale > 0 ? 'text-rose-600 bg-rose-50 border-rose-100' : 'text-yellow-700 bg-yellow-50 border-yellow-100',
        description: 'Offers without an offer_letter_date. Stale = waiting ≥ 3 days.',
      });
    }
    if (m.pending_joining_count != null && m.pending_joining_count > 0) {
      const stale = m.pending_joining_stale_count || 0;
      tiles.push({
        key: 'pending_joining', label: 'Pending joining',
        badge: stale > 0 ? `${stale} overdue` : null, value: m.pending_joining_count, icon: CalendarClock,
        color: stale > 0 ? 'text-fuchsia-600 bg-fuchsia-50 border-fuchsia-100' : 'text-amber-700 bg-amber-50 border-amber-100',
        description: 'Past joining_date but never marked as Joined.',
      });
    }
    if (m.joined_ctc_total != null && showRevenue) {
      tiles.push({
        key: 'joined_ctc_total', label: 'Joined CTC',
        value: formatINR(m.joined_ctc_total) || '—', icon: IndianRupee,
        color: 'text-rose-600 bg-rose-50 border-rose-100',
        description: 'Sum of offered_ctc for joins inside the current window.',
      });
    }
    return tiles;
  }, [metrics, showRevenue]);

  // ---------- Customize: pin/hide tiles (persisted) ----------
  const TILE_PREF_KEY = 'cc-tile-prefs-v1';
  const [hiddenTileKeys, setHiddenTileKeys] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(TILE_PREF_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const persistHidden = useCallback((next) => {
    setHiddenTileKeys(next);
    try { localStorage.setItem(TILE_PREF_KEY, JSON.stringify(next)); } catch { /* quota */ }
  }, []);
  const toggleTileHidden = useCallback((key) => {
    const next = hiddenTileKeys.includes(key)
      ? hiddenTileKeys.filter((k) => k !== key)
      : [...hiddenTileKeys, key];
    persistHidden(next);
  }, [hiddenTileKeys, persistHidden]);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const { pinnedTiles, hiddenTiles } = useMemo(() => {
    const pinned = []; const hidden = [];
    for (const t of metricTiles) {
      if (hiddenTileKeys.includes(t.key)) hidden.push(t); else pinned.push(t);
    }
    return { pinnedTiles: pinned, hiddenTiles: hidden };
  }, [metricTiles, hiddenTileKeys]);

  // ---------- Drill-down dialog ----------
  const [drillMetric, setDrillMetric] = useState(null);
  const [drillItems, setDrillItems] = useState([]);
  const [drillSummary, setDrillSummary] = useState({});
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSavingId, setDrillSavingId] = useState(null);
  const [drillDropConfirmId, setDrillDropConfirmId] = useState(null);

  const openDrillDown = useCallback(async (metricKey) => {
    if (!DRILLABLE_METRICS.includes(metricKey)) return;
    setDrillMetric(metricKey);
    setDrillItems([]); setDrillSummary({}); setDrillLoading(true);
    try {
      const range = ccRangeActive && ccStart && ccEnd && ccStart <= ccEnd
        ? { start_date: ccStart, end_date: ccEnd } : null;
      const res = await commandCenterApi.getMetricItems(metricKey, 100, range);
      setDrillItems(res.data?.items || []);
      setDrillSummary(res.data?.summary || {});
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load details'));
      setDrillMetric(null);
    } finally {
      setDrillLoading(false);
    }
  }, [ccRangeActive, ccStart, ccEnd]);

  // Row click → jump to Command Center with the candidate selected.
  const handleDrillRowClick = useCallback((item) => {
    if (!item) return;
    const params = new URLSearchParams();
    if (item.job_id) params.set('job', item.job_id);
    if (item.application_id) params.set('app', item.application_id);
    navigate(`/workbench?${params.toString()}`);
    setDrillMetric(null);
  }, [navigate]);

  // One-click Mark Joined / Mark Dropped on Pending Joining rows.
  const markAsJoinedFromDrill = useCallback(async (item, e) => {
    if (e) e.stopPropagation();
    if (!item?.application_id || drillSavingId) return;
    setDrillSavingId(item.application_id);
    try {
      await applicationsApi.changeStage(item.application_id, {
        stage: 'joined', joining_date: item.date,
        remarks: 'Marked as joined from Pending Joining drill-down',
      });
      toast.success(`${item.candidate_name || 'Candidate'} marked as Joined`);
      setDrillItems((prev) => prev.filter((x) => x.application_id !== item.application_id));
      fetchMetrics();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as joined'));
    } finally {
      setDrillSavingId(null);
    }
  }, [drillSavingId, fetchMetrics]);

  const markAsDroppedFromDrill = useCallback(async (item, e) => {
    if (e) e.stopPropagation();
    if (!item?.application_id || drillSavingId) return;
    setDrillSavingId(item.application_id);
    try {
      await applicationsApi.changeStage(item.application_id, {
        stage: 'dropped_out', dropped_reason: 'lost_contact',
        remarks: 'Marked as dropped (ghosted offer) from Pending Joining drill-down',
      });
      toast.success(`${item.candidate_name || 'Candidate'} marked as Dropped`);
      setDrillItems((prev) => prev.filter((x) => x.application_id !== item.application_id));
      fetchMetrics();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark as dropped'));
    } finally {
      setDrillSavingId(null);
      setDrillDropConfirmId(null);
    }
  }, [drillSavingId, fetchMetrics]);

  // Mobile collapse state — on < md viewports we render a compact
  // "Today's snapshot" pill (with the 3 most-actionable KPIs) and let the
  // user tap to expand to the full strip. Persisted so a power user who
  // always expands isn't re-collapsing every page load.
  const MOBILE_EXPAND_KEY = 'cc-summary-mobile-expanded';
  const [mobileExpanded, setMobileExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(MOBILE_EXPAND_KEY) === '1';
    } catch { return false; }
  });
  const toggleMobileExpanded = useCallback(() => {
    setMobileExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem(MOBILE_EXPAND_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // Compact "today snapshot" — limited to the 3 highest-signal KPIs
  // (subs / interviews / closures) so the pill stays one row on small
  // viewports. Falls back gracefully if any are hidden via the customize
  // popover (no harm — that user explicitly opted them out).
  const snapshotKeys = ['submissions', 'interviews', 'closures'];
  const snapshotTiles = pinnedTiles.filter((t) => snapshotKeys.includes(t.key));

  return (
    <div className="space-y-3" data-testid="cc-summary-strip">
      <TodaysWinBanner metrics={metrics} user={user} />

      {/* Mobile-only compact snapshot pill — tap to expand. Hidden ≥ md.
          Renders the same data the full strip below would render, so the
          backend fetch isn't redundant. */}
      <div className="md:hidden" data-testid="cc-summary-mobile-pill">
        <button
          type="button"
          onClick={toggleMobileExpanded}
          className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50 transition focus:outline-none focus:ring-2 focus:ring-teal-300"
          data-testid="cc-summary-mobile-toggle"
          aria-expanded={mobileExpanded}
          aria-controls="cc-summary-full-strip"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1 overflow-x-auto">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold flex-shrink-0">
              Today
            </span>
            {metricsLoading && metrics === null ? (
              <span className="inline-block w-32 h-3 bg-slate-200 rounded animate-pulse" />
            ) : snapshotTiles.length > 0 ? (
              snapshotTiles.map((t) => {
                const Icon = t.icon;
                return (
                  <span
                    key={t.key}
                    className="inline-flex items-center gap-1.5 text-xs flex-shrink-0"
                    data-testid={`cc-summary-mobile-${t.key}`}
                  >
                    <Icon className="w-3 h-3 text-slate-500" />
                    <span className="font-bold text-slate-900 tabular-nums">{t.value}</span>
                    <span className="text-slate-500">{t.label.split(' ')[0]}</span>
                  </span>
                );
              })
            ) : (
              <span className="text-xs text-slate-400 italic">No snapshot tiles pinned</span>
            )}
          </div>
          <span className="text-[10px] font-semibold text-teal-700 flex-shrink-0">
            {mobileExpanded ? 'Collapse' : 'Expand'}
          </span>
        </button>
      </div>

      {/* Date range filter — same shape as the original CC strip.
          Hidden on small screens unless the user expanded the snapshot. */}
      <div
        id="cc-summary-full-strip"
        className={`${mobileExpanded ? 'block' : 'hidden md:block'} space-y-3`}
        data-testid="cc-summary-full-strip"
      >
      <div className="flex items-center gap-2 flex-wrap rounded-lg border border-slate-200 bg-white px-3 py-2"
           data-testid="cc-range-filter">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Date range</span>
        <button type="button" onClick={() => setCcRangeActive(false)} data-testid="cc-range-default"
                className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                  !ccRangeActive ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}>
          Today / This month
        </button>
        <button type="button" onClick={() => setCcRangeActive(true)} data-testid="cc-range-custom"
                className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                  ccRangeActive ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}>
          Custom
        </button>
        {ccRangeActive && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">From</span>
            <input type="date" value={ccStart} max={ccEnd} onChange={(e) => setCcStart(e.target.value)}
                   className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
                   data-testid="cc-range-start" />
            <span className="text-xs text-slate-500">to</span>
            <input type="date" value={ccEnd} min={ccStart} max={new Date().toISOString().slice(0, 10)}
                   onChange={(e) => setCcEnd(e.target.value)}
                   className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
                   data-testid="cc-range-end" />
            {ccStart > ccEnd ? (
              <span className="text-xs text-red-600 font-medium">Start must be ≤ End</span>
            ) : null}
          </div>
        )}
      </div>

      {/* KPI tiles strip */}
      <div className="grid gap-2"
           style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
           data-testid="cc-metrics-strip">
        {pinnedTiles.map((t) => {
          const Icon = t.icon;
          const isClickable = DRILLABLE_METRICS.includes(t.key);
          const TileTag = isClickable ? 'button' : 'div';
          return (
            <TileTag key={t.key} type={isClickable ? 'button' : undefined}
                     onClick={isClickable ? () => openDrillDown(t.key) : undefined}
                     disabled={isClickable && metricsLoading && metrics === null}
                     data-testid={`cc-metric-${t.key}`}
                     aria-label={isClickable ? `View ${t.label} details` : undefined}
                     className={`text-left rounded-lg border ${t.color} px-3 py-2 flex items-center gap-2.5 transition ${
                       isClickable
                         ? 'hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-current/40 cursor-pointer'
                         : 'hover:shadow-sm'
                     }`}>
              <div className="w-8 h-8 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 truncate font-medium flex items-center gap-1">
                  <span className="truncate" title={t.label}>{t.label}</span>
                  {t.description ? (
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span role="button" tabIndex={0} aria-label={`What is ${t.label}?`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-slate-400 hover:text-slate-700 flex-shrink-0">
                            <Info className="w-3 h-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[300px] text-[11px] leading-relaxed">
                          <p>{t.description}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null}
                  {isClickable ? <ExternalLink className="w-2.5 h-2.5 text-slate-400 flex-shrink-0 ml-auto" /> : null}
                </div>
                <div className="text-base font-bold text-slate-900 leading-tight truncate flex items-baseline gap-1.5">
                  {metricsLoading && metrics === null ? (
                    <span className="inline-block w-8 h-3 bg-slate-200 rounded animate-pulse" />
                  ) : (
                    <>
                      <span className="truncate">{t.value}</span>
                      {t.badge ? (
                        <span className="text-[9px] font-semibold uppercase tracking-wide px-1 py-0.5 rounded bg-white/60 text-rose-700 flex-shrink-0">
                          {t.badge}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </TileTag>
          );
        })}

        {/* Customize / +N more */}
        {(hiddenTiles.length > 0 || metricTiles.length > 0) && (
          <Popover open={customizeOpen} onOpenChange={setCustomizeOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="cc-customize-tiles-trigger"
                      className="text-left rounded-lg border border-dashed border-slate-300 bg-slate-50/60 hover:bg-slate-100 px-3 py-2 flex items-center gap-2.5 transition focus:outline-none focus:ring-2 focus:ring-slate-300">
                <div className="w-8 h-8 rounded-md bg-white flex items-center justify-center flex-shrink-0">
                  <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                    {hiddenTiles.length > 0 ? `+${hiddenTiles.length} more` : 'Customize'}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {hiddenTiles.length > 0 ? hiddenTiles.map((t) => t.label).join(', ') : 'Pin your KPIs'}
                  </div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-3" data-testid="cc-customize-tiles-popover">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                Pin / hide tiles
              </div>
              <ul className="space-y-0.5 max-h-72 overflow-y-auto">
                {metricTiles.map((t) => {
                  const isPinned = !hiddenTileKeys.includes(t.key);
                  const Icon = t.icon;
                  return (
                    <li key={t.key}>
                      <label className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer text-xs"
                             data-testid={`cc-customize-tile-${t.key}`}>
                        <input type="checkbox" checked={isPinned} onChange={() => toggleTileHidden(t.key)}
                               className="w-3.5 h-3.5 accent-teal-600" aria-label={`Pin ${t.label}`} />
                        <Icon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                        <span className="flex-1 truncate text-slate-700">{t.label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </PopoverContent>
          </Popover>
        )}
      </div>
      </div>{/* /cc-summary-full-strip */}

      {/* Drill-down dialog */}
      <Dialog open={drillMetric !== null}
              onOpenChange={(open) => { if (!open) { setDrillMetric(null); setDrillItems([]); setDrillSummary({}); setDrillDropConfirmId(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col p-0 gap-0" data-testid="cc-drill-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <DialogTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              {drillMetric ? DRILL_TITLES[drillMetric] : ''}
              {!drillLoading && drillItems.length > 0 ? (
                <Badge variant="secondary" className="text-xs" data-testid="cc-drill-count">{drillItems.length}</Badge>
              ) : null}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Click a row to open that candidate in the Command Center.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-5 py-3 flex-1" data-testid="cc-drill-list">
            {drillLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : drillItems.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="cc-drill-empty">
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm font-medium">Nothing here yet</p>
              </div>
            ) : (
              <ul className="space-y-1.5">
                {drillItems.map((it, idx) => {
                  const showJoinedAction = drillMetric === 'pending_joining';
                  const savingThis = drillSavingId === it.application_id;
                  return (
                    <li key={(it.application_id || idx) + '-' + idx}>
                      <div role="button" tabIndex={0}
                           onClick={() => handleDrillRowClick(it)}
                           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDrillRowClick(it); } }}
                           data-testid={`cc-drill-row-${idx}`}
                           className="w-full text-left rounded-md border border-slate-200 hover:border-teal-300 hover:bg-teal-50/40 transition px-3 py-2.5 group cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-300">
                        <div className="flex items-start gap-3">
                          <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5 text-slate-500">
                            <User className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-900 truncate">
                                {it.candidate_name || 'Unnamed'}
                              </span>
                              {it.value_label ? (
                                <Badge variant="outline" className="text-[10px] ml-auto">{it.value_label}</Badge>
                              ) : null}
                            </div>
                            <div className="text-xs text-slate-500 truncate mt-0.5">
                              {it.job_title || '—'}{it.client_name ? ` · ${it.client_name}` : ''}
                            </div>
                          </div>
                          {showJoinedAction ? (
                            drillDropConfirmId === it.application_id ? (
                              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" type="button" onClick={(e) => markAsDroppedFromDrill(it, e)}
                                        disabled={savingThis || !!drillSavingId}
                                        data-testid={`cc-drill-confirm-drop-${idx}`}
                                        className="h-7 px-2.5 text-[11px] bg-rose-600 hover:bg-rose-700 text-white">
                                  {savingThis ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                  Confirm drop
                                </Button>
                                <Button size="sm" variant="ghost" type="button"
                                        onClick={(e) => { e.stopPropagation(); setDrillDropConfirmId(null); }}
                                        disabled={savingThis}>Cancel</Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                <Button size="sm" type="button"
                                        onClick={(e) => markAsJoinedFromDrill(it, e)}
                                        disabled={savingThis || !!drillSavingId}
                                        data-testid={`cc-drill-mark-joined-${idx}`}
                                        className="h-7 px-2.5 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white">
                                  {savingThis ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                                  Mark joined
                                </Button>
                                <Button size="sm" variant="outline" type="button"
                                        onClick={(e) => { e.stopPropagation(); setDrillDropConfirmId(it.application_id); }}
                                        disabled={savingThis || !!drillSavingId}
                                        data-testid={`cc-drill-mark-dropped-${idx}`}
                                        className="h-7 px-2.5 text-[11px] border-rose-200 text-rose-700 hover:bg-rose-50">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Mark dropped
                                </Button>
                              </div>
                            )
                          ) : (
                            <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal-500 flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-slate-100 bg-slate-50/50">
            <Button variant="outline" size="sm" onClick={() => setDrillMetric(null)} data-testid="cc-drill-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommandCenterSummary;
